import {
  createRootRoute,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { lazy, useEffect } from 'react';

// Zero bytes in production — static import would bundle it regardless of the
// DEV guard because tree-shaking can't eliminate side-effectful imports.
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/router-devtools').then(m => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

import { createPortal } from 'react-dom';
import { Toaster } from 'sonner';
import { fetchMe } from '@/api/me';
import AppHeader from '@/components/shared/AppHeader';
import DocsHeader from '@/components/shared/DocsHeader';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import NavigationProgress from '@/components/shared/NavigationProgress';
import Sidebar from '@/components/shared/Sidebar';
import { UploadGuard } from '@/components/shared/UploadGuard';
import { useBreadcrumbTrail } from '@/hooks/useBreadcrumbTrail';
import { useIsMobile } from '@/hooks/useIsMobile';
import { seedTablePreferencesFromSession } from '@/hooks/useTablePreferences';
import { useTheme } from '@/hooks/useTheme';
import { OLD_FRONTEND_URL } from '@/lib/constants/api';
import { checkAuthGuard } from '@/lib/utils/auth';
import { setDatadogUser } from '@/lib/utils/datadog';
import { changeLocale } from '@/lib/utils/i18n';
import { useSessionStore } from '@/store/sessionStore';
import { useSidebarStore } from '@/store/sidebarStore';

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // Docs are public — accessible without authentication.
    if (location.pathname.startsWith('/docs')) return;
    const { allowed } = await checkAuthGuard();
    if (!allowed) {
      window.location.href = OLD_FRONTEND_URL;
      return;
    }
    const me = await fetchMe();
    if (me) {
      useSessionStore.getState().setUser(me);
      seedTablePreferencesFromSession(me.preferences);
      const lang =
        (me.preferences?.userLanguage as { language?: string } | undefined)
          ?.language ?? me.langId;
      if (lang) await changeLocale(lang);
    }
  },
  component: RootLayout,
});

// eslint-disable-next-line react-refresh/only-export-components
function RootLayout() {
  const panelOpen = useSidebarStore(s => s.panelOpen);
  const selectedGroupKey = useSidebarStore(s => s.selectedGroupKey);
  const breadcrumbTrail = useBreadcrumbTrail();
  const isMobile = useIsMobile();
  const user = useSessionStore(s => s.user);
  const isDocsRoute = useRouterState({
    select: s => s.location.pathname.startsWith('/docs'),
  });

  // Re-hydrate session when in-memory store is empty (e.g. after Vite HMR
  // replaces the module before any navigation re-runs `beforeLoad`).
  // Skip on public /docs — no session there, and a 401 would bounce to login.
  useEffect(() => {
    if (user || isDocsRoute) return;
    let cancelled = false;
    void (async () => {
      const me = await fetchMe();
      if (cancelled || !me) return;
      useSessionStore.getState().setUser(me);
      seedTablePreferencesFromSession(me.preferences);
      const lang =
        (me.preferences?.userLanguage as { language?: string } | undefined)
          ?.language ?? me.langId;
      if (lang) await changeLocale(lang);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isDocsRoute]);

  // Attach the authenticated user to the RUM session for error/session
  // correlation. No-op in local dev where RUM isn't initialised.
  useEffect(() => {
    if (user) setDatadogUser(user);
  }, [user]);
  const pathname = useRouterState({ select: s => s.location.pathname });
  useTheme();

  const isDocs = pathname.startsWith('/docs');
  const sidebarWidth =
    isDocs || isMobile ? 0 : panelOpen && selectedGroupKey ? 264 : 64;

  return (
    <div className='fixed inset-0 overflow-hidden'>
      <div
        className='h-full overflow-hidden grid transition-[grid-template-columns] duration-200'
        style={{
          gridTemplateColumns: `${sidebarWidth}px 1fr`,
          gridTemplateRows: '52px 1fr',
        }}
      >
        <NavigationProgress />
        {isDocs ? (
          <DocsHeader />
        ) : (
          <AppHeader breadcrumbTrail={breadcrumbTrail} />
        )}
        {!isDocs && <Sidebar />}
        <main className='overflow-hidden row-start-2 col-start-2 flex flex-col min-h-0 h-full bg-background @container'>
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
        <UploadGuard />
        {import.meta.env.DEV && (
          <TanStackRouterDevtools position='bottom-left' />
        )}
      </div>
      {createPortal(
        <Toaster richColors position='bottom-right' />,
        document.body,
      )}
    </div>
  );
}
